import React, { useState } from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import { ArrowLeft, BarChart3, RefreshCw, History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTestSets } from '@/hooks/useTestSets'
import { useEvaluationResults } from '@/hooks/useEvaluationResults'
import { EvaluationResultsView } from '@/components/evaluation/EvaluationResultsView'
import { EvaluationHistory } from '@/components/evaluation/EvaluationHistory'
import { ExportButton } from '@/components/evaluation/ExportButton'
import { PROTECTED_ROUTES } from '@/constants/routes'

const EvaluationResultsPage: React.FC = () => {
  const { testSetId } = useParams<{ testSetId: string }>()
  const { currentTestSet, loading, error, refreshCurrentTestSet } = useTestSets(testSetId)
  const { 
    results, 
    history, 
    loading: resultsLoading, 
    error: resultsError,
    refreshResults
  } = useEvaluationResults(testSetId || '')
  const [activeTab, setActiveTab] = useState('results')

  const handleRefresh = async () => {
    await Promise.all([
      refreshCurrentTestSet(),
      refreshResults()
    ])
  }

  if (!testSetId) {
    return <Navigate to={PROTECTED_ROUTES.EVALUATIONS} replace />
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-8">
          <p className="text-destructive mb-4">{error}</p>
          <Link to={PROTECTED_ROUTES.EVALUATIONS}>
            <Button variant="outline">Back to Evaluations</Button>
          </Link>
        </div>
      </div>
    )
  }

  if (!currentTestSet) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`${PROTECTED_ROUTES.EVALUATIONS}/${testSetId}`}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Test Set
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Evaluation Results</h1>
            <p className="text-muted-foreground mt-1">{currentTestSet.name}</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={handleRefresh}
            disabled={resultsLoading || loading}
          >
            <RefreshCw className={`h-4 w-4 ${resultsLoading || loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <ExportButton 
            testSet={currentTestSet}
            results={results}
            disabled={resultsLoading}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Test Cases</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentTestSet._count.testCases}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Latest Score</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentTestSet.latestRun?.averageScore?.toFixed(1) || 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Runs</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentTestSet._count.evaluationRuns}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentTestSet.latestRun ? (
                <Badge 
                  variant={currentTestSet.latestRun.status === 'COMPLETED' ? 'default' : 
                          currentTestSet.latestRun.status === 'FAILED' ? 'destructive' : 'secondary'}
                >
                  {currentTestSet.latestRun.status}
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">No runs</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results and History Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="results">Latest Results</TabsTrigger>
          <TabsTrigger value="history">Run History</TabsTrigger>
        </TabsList>

        <TabsContent value="results" className="space-y-4">
          {resultsLoading && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-muted-foreground mt-4">Loading results...</p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {resultsError && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <p className="text-destructive mb-4">{resultsError}</p>
                  <Button onClick={refreshResults} variant="outline">
                    Try Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {!resultsLoading && !resultsError && (
            <EvaluationResultsView 
              results={results}
              runSummary={currentTestSet.latestRun && history.length > 0 ? {
                totalCases: history[0].totalCases,
                completedCases: history[0].completedCases,
                passedCases: history[0].passedCases,
                failedCases: history[0].failedCases,
                averageScore: history[0].averageScore || 0,
                passingScore: history[0].passingScore,
                status: history[0].status
              } : undefined}
            />
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <EvaluationHistory testSetId={testSetId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default EvaluationResultsPage